<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">sell</span>
          </div>
          <h4 class="title">{{ $t("offers.title") }}</h4>
        </md-card-header>
        <md-card-content>
          <div class="offers-calc-bar">
            <span v-if="status && status.running" class="offers-calc-state"><md-icon>sync</md-icon> {{ $t("offers.calcRunning") }}</span>
            <span v-else-if="status && status.lastFullOn" class="offers-calc-state">{{ $t("offers.calcStand", { date: formatDateTime(status.lastFullOn) }) }}</span>
            <span v-else class="offers-calc-state">{{ $t("offers.calcNever") }}</span>
            <span v-if="status && status.error" class="offers-calc-error">{{ $t("offers.calcError") }}</span>
            <md-button v-if="hasPermission('Write', 'Offers', 'Edit')" class="md-simple md-sm" :disabled="status && status.running" :title="$t('offers.calcRefreshTip')" @click="refreshCalculations">
              <md-icon>refresh</md-icon> {{ $t("offers.calcRefresh") }}
            </md-button>
          </div>
          <div class="table-sticky">
            <v-server-table :columns="tableColumns" :options="tableOptions" ref="table" class="table-offers" id="table-offers">
              <template #h__sku>
                <SortMenu :label="$t('offers.table.skuContent')" :options="skuSortOptions" :order="currentOrder" @sort="sortBy" />
              </template>
              <template #h__prices>
                <SortMenu :label="$t('offers.table.prices')" :options="priceSortOptions" :order="currentOrder" @sort="sortBy" />
              </template>
              <template #h__margins>
                <SortMenu :label="$t('offers.table.margins')" :options="marginSortOptions" :order="currentOrder" @sort="sortBy" />
              </template>
              <template #actions="{ row }">
                <div class="md-table-cell-container cell-actions">
                  <md-button v-if="hasPermission('Write', 'Offers', 'Edit')" class="md-icon-button" :title="$t('offers.editOffer')" @click="openEdit(row)">
                    <md-icon>edit</md-icon>
                  </md-button>
                  <md-button v-if="hasPermission('Write', 'Offers', 'Create')" class="md-icon-button" :title="$t('offers.copyOffer')" @click="openCopy(row)">
                    <md-icon>content_copy</md-icon>
                  </md-button>
                  <md-button v-if="hasPermission('Write', 'Offers', 'Edit')" class="md-icon-button md-info offer-ai-btn" :title="$t('offers.advice.button')" @click="openAdvice(row)">
                    <img src="/img/ci-ai.png" alt="AI" />
                  </md-button>
                  <md-button v-if="hasPermission('Write', 'Offers', 'Delete')" class="md-icon-button md-danger" :title="$t('offers.deleteOffer')" @click="deleteTarget = row">
                    <md-icon>delete</md-icon>
                  </md-button>
                </div>
              </template>
              <template #image="{ row }">
                <offer-image :offer-id="row.id" :image="row.image" :product-id="row.imageProductId" :alt="row.sku" @zoom="zoomUrl = $event"></offer-image>
              </template>
              <template #sku="{ row }">
                <div class="offer-sku-cell">{{ row.sku }}</div>
                <div class="offer-chips">
                  <span v-for="i in row.items" :key="i.productId" class="offer-chip" :title="`${i.sku} - ${i.name}\n${$t('offers.filterByProduct')}`" @click="filterBy(i.sku)">
                    <b>{{ i.quantity }}&times;</b> {{ i.sku }}
                  </span>
                </div>
              </template>
              <template #prices="{ row }">
                <div class="offer-prices" :title="notesTitle(row)">
                  <span class="offer-prices__label offer-prices__label--avk">AVK:</span>
                  <span class="offer-prices__value">
                    <span v-if="avk(row) != null" class="offer-avk" :class="avkClass(row)">{{ money(avk(row)) }}</span>
                    <span v-else class="offer-muted">–</span>
                  </span>
                  <span class="offer-prices__extra">
                    <!-- leeres title unterdrueckt die Hinweise des Elternelements, sonst verdecken sie den Preisverlauf -->
                    <span v-if="avk(row) != null" class="offer-price-trend" title="" @mouseenter="loadHistory(row.sku)">
                      <md-icon>euro</md-icon>
                      <md-tooltip md-direction="right" class="offer-price-tooltip">
                        <div class="offer-price-tooltip__head">{{ $t("offers.priceChart.title", { sku: row.sku }) }}</div>
                        <div v-if="!histories[row.sku]">{{ $t("priceHistory.loading") }}</div>
                        <div v-else-if="histories[row.sku].error">{{ $t("priceHistory.loadError") }}</div>
                        <template v-else>
                          <price-chart :entries="histories[row.sku].entries" :months="12" :width="340" :height="130"></price-chart>
                          <div class="offer-price-changes">
                            <template v-for="c in recentChanges(row.sku)">
                              <span :key="c.recordedOn + 'd'">{{ formatDate(c.recordedOn) }}</span>
                              <span :key="c.recordedOn + 'p'" class="text-right">{{ money(c.price) }}</span>
                              <span :key="c.recordedOn + 'c'" class="text-right" :class="c.change > 0 ? 'offer-up' : c.change < 0 ? 'offer-down' : ''">
                                {{ c.isStart ? $t("priceHistory.start") : signedMoney(c.change) }}
                              </span>
                            </template>
                          </div>
                        </template>
                      </md-tooltip>
                    </span>
                  </span>
                  <template v-for="f in floors">
                    <span :key="f.key + 'l'" class="offer-prices__label offer-prices__floor">{{ f.label }}:</span>
                    <span :key="f.key + 'v'" class="offer-prices__value offer-prices__floor">{{ moneyUp(calcOf(row, f.field)) }}</span>
                    <span :key="f.key + 'd'" class="offer-prices__extra offer-prices__diff" :class="diffClass(row, f.field)">
                      <span>{{ diffMoney(row, f.field) }}</span>
                      <span>{{ diffPercent(row, f.field) }}</span>
                    </span>
                  </template>
                </div>
              </template>
              <template #margins="{ row }">
                <table class="offer-margins" :title="notesTitle(row)">
                  <tbody>
                    <tr>
                      <td class="offer-margins__label">{{ $t("offers.table.ekBep") }}</td>
                      <td class="offer-margins__value">{{ usd(calcOf(row, "purchasePriceUsd")) }} / {{ money(calcOf(row, "purchasePriceEur")) || "–" }} / {{ money(calcOf(row, "bep")) || "–" }}</td>
                    </tr>
                    <tr v-for="m in marginRows" :key="m.key">
                      <td class="offer-margins__label">{{ m.label }}</td>
                      <td class="offer-margins__value" :class="signClass(calcOf(row, m.key))">{{ eurPct(calcOf(row, m.key), calcOf(row, m.key + "Percent")) }}</td>
                    </tr>
                    <tr>
                      <td class="offer-margins__label">{{ $t("offers.table.vk") }}</td>
                      <td class="offer-margins__value">
                        {{ money(calcOf(row, "saleGross")) || "–" }} / {{ money(calcOf(row, "saleNet")) || "–" }}
                        <!-- Klick oeffnet die gespeicherte Kalkulation als Lightbox (Trello KWDTD2pU, Update e) -->
                        <span v-if="row.calculation" class="offer-calc-info" @click="openCalc(row)">
                          <md-icon>info</md-icon>
                          <md-tooltip md-direction="left">{{ $t("procurement.calcDialog.open") }}</md-tooltip>
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </template>
              <template #stock="{ row }">
                <offer-stock-cell :offer="row"></offer-stock-cell>
              </template>
            </v-server-table>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <div class="sp-dock">
      <a v-if="hasPermission('Write', 'Offers', 'Create')" class="sp-dock-trigger" :title="$t('offers.createOffer')" @click="openCreate">
        <md-icon>add</md-icon>
      </a>
      <div class="sp-dock-search">
        <a class="sp-dock-trigger" :class="{ 'sp-dock-trigger--active': searchActive }" :title="$t('offers.searchTitle')" @click="toggleDockSearch">
          <md-icon>search</md-icon>
        </a>
        <div v-show="dockSearchOpen" class="sp-dock-search-panel">
          <md-field md-clearable>
            <label>{{ $t("offers.searchGeneral") }}</label>
            <md-input ref="dockSearchInput" v-model="search.keywords"></md-input>
          </md-field>
          <md-field md-clearable>
            <label>{{ $t("offers.sku") }}</label>
            <md-input v-model="search.offerSku"></md-input>
          </md-field>
          <md-field md-clearable>
            <label>{{ $t("offers.productSku") }}</label>
            <md-input v-model="search.productSku"></md-input>
          </md-field>
          <md-switch v-model="search.exactMatchSku">{{ $t("products.exactMatch") }}</md-switch>
        </div>
      </div>
      <offer-filter-drawer @apply-filters="onDrawerFilters"></offer-filter-drawer>
    </div>

    <offer-dialog v-if="dialog.show" :offer="dialog.offer" :copy="dialog.copy" @close="dialog.show = false" @saved="onSaved" @image-changed="reload"></offer-dialog>
    <image-zoom :url="zoomUrl" @close="zoomUrl = null"></image-zoom>

    <modal v-if="deleteTarget" @close="deleteTarget = null">
      <template slot="header">
        <h4 class="modal-title">{{ $t("offers.deleteOffer") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="deleteTarget = null">
          <md-icon>clear</md-icon>
        </md-button>
      </template>
      <template slot="body">{{ $t("offers.deleteConfirm", { sku: deleteTarget.sku }) }}</template>
      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="deleteTarget = null">{{ $t("offers.close") }}</md-button>
        <md-button class="md-danger" @click="confirmDelete">{{ $t("offers.delete") }}</md-button>
      </template>
    </modal>

    <modal v-if="calcDialog.offer" class-name="offer-calc-modal" @close="calcDialog.offer = null">
      <template slot="header">
        <h4 class="modal-title">{{ calcDialog.offer.sku }} – {{ $t("procurement.calcDialog.title") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="calcDialog.offer = null"><md-icon>clear</md-icon></md-button>
      </template>
      <template slot="body">
        <div class="offer-calc-dialog">
          <div v-if="calcDialog.state === 'error'" class="offer-muted">{{ $t("procurement.priceCell.calcError") }}</div>
          <div v-else-if="!calcDialog.calc" class="offer-muted">{{ $t("procurement.priceCell.calcLoading") }}</div>
          <offer-calc-detail v-else :calc="calcDialog.calc" />
        </div>
      </template>
    </modal>
    <modal v-if="advice.target" class-name="offer-advice-modal" @close="closeAdvice">
      <template slot="header">
        <h4 class="modal-title">{{ $t("offers.advice.title", { sku: advice.target.sku }) }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="closeAdvice">
          <md-icon>clear</md-icon>
        </md-button>
      </template>
      <template slot="body">
        <p v-if="advice.state === 'confirm'">{{ $t("offers.advice.confirm", { sku: advice.target.sku }) }}</p>
        <div v-else-if="advice.state === 'loading'" class="offer-advice-loading">
          <md-progress-spinner md-mode="indeterminate" :md-diameter="28" :md-stroke="3"></md-progress-spinner>
          <span>{{ $t("offers.advice.loading") }}</span>
        </div>
        <p v-else-if="advice.state === 'error'" class="offer-advice-error">{{ advice.error }}</p>
        <div v-else-if="advice.result" class="offer-advice-result">
          <div v-if="advice.result.recommendedPriceGross != null" class="offer-advice-price">
            {{ $t("offers.advice.recommendedPrice") }} <b>{{ money(advice.result.recommendedPriceGross) }}</b>
            <span v-if="avk(advice.target) != null" class="offer-muted"> ({{ $t("offers.advice.currentPrice", { price: money(avk(advice.target)) }) }})</span>
          </div>
          <div v-if="advice.result.situation" class="offer-advice-situation">
            <span class="offer-advice-chip">{{ $t("offers.advice.situation." + advice.result.situation) }}</span>
            <span v-if="advice.result.minPriceGross != null" class="offer-muted">
              {{ $t("offers.advice.range", { min: money(advice.result.minPriceGross), max: advice.result.maxPriceGross != null ? money(advice.result.maxPriceGross) : "–" }) }}
            </span>
          </div>
          <p v-if="advice.result.priceCorrected" class="offer-advice-corrected">
            {{ advice.result.aiPriceGross != null ? $t("offers.advice.corrected", { price: money(advice.result.aiPriceGross) }) : $t("offers.advice.noAiPrice") }}
          </p>
          <div class="offer-advice-text" v-html="adviceHtml"></div>
          <p class="offer-advice-hint">{{ $t("offers.advice.hint", { date: formatDateTime(advice.result.generatedOn) }) }}</p>
        </div>
      </template>
      <template slot="footer">
        <template v-if="advice.state === 'confirm'">
          <md-button class="md-secondary mr-1" @click="closeAdvice">{{ $t("offers.advice.no") }}</md-button>
          <md-button class="md-info" @click="runAdvice">{{ $t("offers.advice.yes") }}</md-button>
        </template>
        <md-button v-else class="md-secondary" :disabled="advice.state === 'loading'" @click="closeAdvice">{{ $t("offers.close") }}</md-button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Modal } from "@/components";
import ImageZoom from "@/components/Products/ImageZoom.vue";
import OfferCalcDetail from "@/components/OfferCalcDetail.vue";
import OfferImage from "@/components/Offers/OfferImage.vue";
import OfferStockCell from "@/components/Offers/OfferStockCell.vue";
import PriceChart from "@/components/PriceChart.vue";
import { EventBus } from "@/helpers/event-bus";
import { hasPermission } from "../../mixins/auth";
import offersService from "../../services/offers.service";
import priceHistoryService from "../../services/price-history.service";
import SortMenu from "../Products/SortMenu.vue";
import OfferDialog from "./OfferDialog.vue";
import OfferFilterDrawer from "./OfferFilterDrawer.vue";

const emptySearch = () => ({ keywords: "", offerSku: "", productSku: "", exactMatchSku: false });

export default {
  components: { Modal, ImageZoom, OfferCalcDetail, OfferImage, OfferStockCell, PriceChart, SortMenu, OfferDialog, OfferFilterDrawer },
  data() {
    return {
      calcDialog: { offer: null, calc: null, state: "" },
      tableColumns: ["actions", "image", "sku", "prices", "margins", "stock"],
      tableOptions: {
        headings: {
          actions: () => this.$t("offers.actions"),
          image: () => this.$t("offers.table.image"),
          stock: () => this.$t("offers.table.stock"),
        },
        perPage: 25,
        perPageValues: [25, 100, 5000],
        filterable: false,
        // Sortiert wird ueber die Menues in den Spaltenkoepfen (mehrere Werte je Spalte)
        sortable: [],
        sortIcon: { is: "fa-sort", base: "fa", up: "fa-sort-up", down: "fa-sort-down" },
        orderBy: { column: "sku", ascending: true },
        resizableColumns: false,
        requestFunction: this.requestFunction,
        responseAdapter: this.responseFunction,
        pagination: { show: true },
        texts: {
          count: this.$t("table.count"),
          limit: this.$t("table.records"),
          noResults: this.$t("offers.noOffers"),
        },
        columnsClasses: { sku: "text-left offer-col-sku", prices: "text-left", margins: "text-left", stock: "text-left" },
      },
      currentOrder: { column: "sku", ascending: true },
      search: emptySearch(),
      drawerFilters: { kpiFilters: null, priceBelow: null },
      histories: {},
      status: null,
      statusTimer: null,
      dockSearchOpen: false,
      dialog: { show: false, offer: null, copy: false },
      deleteTarget: null,
      zoomUrl: null,
      advice: { target: null, state: "", result: null, error: "", requestId: 0 },
      searchTimer: null,
    };
  },
  computed: {
    searchActive() {
      return !!(this.search.keywords || this.search.offerSku || this.search.productSku);
    },
    floors() {
      return [
        { key: "kpu", label: "KPU", field: "floorDb1Gross" },
        { key: "opu", label: "OPU", field: "floorDb2Gross" },
        { key: "lpu", label: "LPU", field: "floorDb3Gross" },
      ];
    },
    marginRows() {
      return [
        { key: "grossProfit", label: this.$t("offers.marginGrossProfit") },
        { key: "db1", label: "DB1" },
        { key: "db2", label: "DB2" },
        { key: "db3", label: "DB3" },
      ];
    },
    skuSortOptions() {
      return [{ key: "sku", label: this.$t("offers.sku") }];
    },
    priceSortOptions() {
      return [
        { key: "saleGross", label: "AVK" },
        { key: "kpu", label: "KPU" },
        { key: "opu", label: "OPU" },
        { key: "lpu", label: "LPU" },
        { key: "kpuDiff", label: this.$t("offers.sort.diff", { floor: "KPU" }) },
        { key: "opuDiff", label: this.$t("offers.sort.diff", { floor: "OPU" }) },
        { key: "lpuDiff", label: this.$t("offers.sort.diff", { floor: "LPU" }) },
      ];
    },
    marginSortOptions() {
      const gp = this.$t("offers.marginGrossProfit");
      return [
        { key: "purchasePriceEur", label: this.$t("offers.filter.ek") },
        { key: "bep", label: "BEP" },
        { key: "grossProfit", label: `${gp} (€)` },
        { key: "grossProfitPercent", label: `${gp} (%)` },
        { key: "db1", label: "DB1 (€)" },
        { key: "db1Percent", label: "DB1 (%)" },
        { key: "db2", label: "DB2 (€)" },
        { key: "db2Percent", label: "DB2 (%)" },
        { key: "db3", label: "DB3 (€)" },
        { key: "db3Percent", label: "DB3 (%)" },
        { key: "saleGross", label: this.$t("offerCalculation.saleGross") },
        { key: "saleNet", label: this.$t("offerCalculation.saleNet") },
      ];
    },
    adviceHtml() {
      return this.advice.result ? renderAdvice(this.advice.result.advice) : "";
    },
  },
  watch: {
    search: {
      deep: true,
      handler() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(this.applyFilters, 300);
      },
    },
  },
  created() {
    this.loadStatus();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.statusTimer);
  },
  updated() {
    EventBus.$emit("scroll:changed");
  },
  methods: {
    hasPermission(permissionName, tableName, columnName) {
      return hasPermission(permissionName, tableName, columnName);
    },
    requestFunction(data) {
      const { page, limit } = data;
      const s = this.search;
      return offersService
        .getOffers({
          pageIndex: page,
          pageSize: limit,
          keywords: s.keywords.trim() || undefined,
          offerSku: s.offerSku.trim() || undefined,
          productSku: s.productSku.trim() || undefined,
          exactMatchSku: s.exactMatchSku || undefined,
          kpiFilters: this.drawerFilters.kpiFilters || undefined,
          priceBelow: this.drawerFilters.priceBelow || undefined,
          orderBy: this.currentOrder.column,
          ascending: this.currentOrder.ascending ? 1 : 0,
        })
        .then((res) => ({ data: { data: res.data || [], total: res.total || 0 } }))
        .catch(() => {
          this.$notify({ timeout: 2500, message: this.$t("offers.messages.loadFailed"), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type: "danger" });
          return { data: { data: [], total: 0 } };
        });
    },
    responseFunction({ data }) {
      return { data: data.data, count: data.total };
    },
    sortBy(column, ascending) {
      this.currentOrder = { column, ascending };
      this.applyFilters();
    },
    onDrawerFilters(filters) {
      this.drawerFilters = filters;
      this.applyFilters();
    },
    openCalc(row) {
      this.calcDialog = { offer: row, calc: null, state: "loading" };
      offersService
        .getOfferCalculation(row.id)
        .then((calc) => {
          if (this.calcDialog.offer === row) this.calcDialog = { offer: row, calc, state: "" };
        })
        .catch(() => {
          if (this.calcDialog.offer === row) this.calcDialog = { offer: row, calc: null, state: "error" };
        });
    },
    calcOf(row, key) {
      return row.calculation ? row.calculation[key] : null;
    },
    // Aktueller VK = Brutto-VK der Kalkulation (am Angebot oder aus der Preishistorie); ohne Kalkulation der Afterbuy-Preis
    avk(row) {
      const gross = this.calcOf(row, "saleGross");
      if (gross != null) return gross;
      return row.salePriceGross != null ? row.salePriceGross : row.afterbuyPrice;
    },
    // Ampel: rot unter KPU, orange bis OPU, gelb bis LPU, gruen ab LPU (fehlende Grenzen faerben nicht)
    avkClass(row) {
      const avk = this.avk(row);
      const [kpu, opu, lpu] = ["floorDb1Gross", "floorDb2Gross", "floorDb3Gross"].map((f) => this.calcOf(row, f));
      if (avk == null || kpu == null) return "";
      if (avk < kpu) return "offer-avk--red";
      if (opu == null) return "";
      if (avk < opu) return "offer-avk--orange";
      if (lpu == null) return "";
      return avk < lpu ? "offer-avk--yellow" : "offer-avk--green";
    },
    diff(row, field) {
      const avk = this.avk(row);
      const floor = this.calcOf(row, field);
      return avk == null || floor == null ? null : avk - Math.ceil(floor * 100 - 1e-9) / 100;
    },
    diffMoney(row, field) {
      const d = this.diff(row, field);
      return d == null ? "" : this.signedMoney(d);
    },
    diffPercent(row, field) {
      const d = this.diff(row, field);
      const avk = this.avk(row);
      if (d == null || !avk) return "";
      const pct = (100 * d) / avk;
      return `${pct > 0 ? "+" : ""}${this.$options.filters.formatUnit(pct, "", 1, 1).trim()} %`;
    },
    diffClass(row, field) {
      const d = this.diff(row, field);
      if (d == null) return "";
      return d >= 0 ? "offer-up" : "offer-down";
    },
    signClass(value) {
      if (value == null) return "";
      return value >= 0 ? "offer-plus" : "offer-minus";
    },
    notesTitle(row) {
      const notes = row.calculation && row.calculation.notes ? row.calculation.notes.split(",") : [];
      // Die Uebersicht kennt das Datum aus der Preishistorie nicht - dafuer eigene Texte ohne {date}
      const key = (n) => (this.$te(`offers.calcNotes.${n}`) ? `offers.calcNotes.${n}` : `offerCalculation.notes.${n}`);
      const texts = notes.filter((n) => this.$te(key(n))).map((n) => this.$t(key(n)));
      if (!row.calculation) return this.$t("offers.calcNotCalculated");
      return texts.join("\n");
    },
    eurPct(eur, pct) {
      if (eur == null && pct == null) return "–";
      return `${eur == null ? "–" : this.money(eur)} / ${pct == null ? "–" : this.$options.filters.formatUnit(pct, "%", 1, 1)}`;
    },
    // Betraege immer mit zwei Nachkommastellen (formatNumberGerman kuerzt 15,70 auf 15,7)
    amount(value) {
      return this.$options.filters.formatUnit(value, "", 2, 2).trim();
    },
    money(value) {
      return value == null ? "" : `${this.amount(value)} €`;
    },
    signedMoney(value) {
      return `${value > 0 ? "+" : ""}${this.amount(value)} €`;
    },
    usd(value) {
      return value == null ? "–" : `${this.amount(value)} USD`;
    },
    // Preisuntergrenze auf den vollen Cent aufgerundet, damit der Preis den Deckungsbeitrag sicher erreicht
    moneyUp(value) {
      return value == null ? "–" : `${this.amount(Math.ceil(value * 100 - 1e-9) / 100)} €`;
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString(this.$i18n.locale) : "";
    },
    formatDateTime(value) {
      return value ? new Date(value).toLocaleString(this.$i18n.locale) : "";
    },
    loadHistory(sku) {
      if (!sku || this.histories[sku]) return;
      priceHistoryService
        .getHistory(sku)
        .then((h) => this.$set(this.histories, sku, { entries: (h && h.entries) || [] }))
        .catch(() => this.$set(this.histories, sku, { entries: [], error: true }));
    },
    // Preisaenderungen der letzten 12 Monate (neueste zuerst), hoechstens 10 Zeilen
    recentChanges(sku) {
      const h = this.histories[sku];
      if (!h) return [];
      const since = Date.now() - 365 * 24 * 60 * 60 * 1000;
      return h.entries.filter((e) => new Date(e.recordedOn).getTime() >= since).slice(0, 10);
    },
    loadStatus() {
      return offersService
        .getCalculationStatus()
        .then((status) => {
          const wasRunning = this.status && this.status.running;
          this.status = status;
          if (status.running || status.queued > 0) {
            clearTimeout(this.statusTimer);
            this.statusTimer = setTimeout(this.loadStatus, 2000);
          } else if (wasRunning) {
            this.reload();
          }
        })
        .catch(() => {});
    },
    refreshCalculations() {
      return offersService
        .refreshCalculations()
        .then((status) => {
          this.status = { ...status, running: true };
          clearTimeout(this.statusTimer);
          this.statusTimer = setTimeout(this.loadStatus, 1500);
        })
        .catch((e) => {
          if (e && e.response && e.response.status === 403) return;
          this.$notify({ timeout: 2500, message: this.$t("offers.calcRefreshFailed"), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type: "danger" });
        });
    },
    // Zur ersten Seite; setPage laedt selbst neu, nur auf Seite 1 braucht es refresh (sonst zwei Anfragen)
    applyFilters() {
      const table = this.$refs.table;
      if (table.Page !== 1) table.setPage(1);
      else table.refresh();
    },
    reload() {
      this.$refs.table.refresh();
    },
    toggleDockSearch() {
      this.dockSearchOpen = !this.dockSearchOpen;
      if (this.dockSearchOpen) {
        this.$nextTick(() => {
          const input = this.$refs.dockSearchInput && this.$refs.dockSearchInput.$el;
          if (input) input.focus();
        });
      }
    },
    filterBy(sku) {
      this.search = { ...emptySearch(), productSku: sku, exactMatchSku: true };
      this.dockSearchOpen = true;
    },
    openCreate() {
      this.dialog = { show: true, offer: null, copy: false };
    },
    openEdit(offer) {
      this.dialog = { show: true, offer, copy: false };
    },
    openCopy(offer) {
      this.dialog = { show: true, offer, copy: true };
    },
    onSaved() {
      this.dialog.show = false;
      this.reload();
      // Die Kalkulation des gespeicherten Angebots wird im Hintergrund neu berechnet; danach die Liste auffrischen.
      this.status = { ...(this.status || {}), running: true };
      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(this.loadStatus, 1500);
    },
    confirmDelete() {
      const target = this.deleteTarget;
      this.deleteTarget = null;
      offersService
        .deleteOffer(target.id)
        .then(() => {
          this.$notify({ timeout: 2500, message: this.$t("offers.messages.deleted"), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type: "success" });
          this.reload();
        })
        .catch((e) => {
          if (e && e.response && e.response.status === 403) return;
          this.$notify({ timeout: 2500, message: this.$t("offers.messages.deleteFailed"), icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type: "danger" });
        });
    },
    openAdvice(row) {
      this.advice = { target: row, state: "confirm", result: null, error: "", requestId: this.advice.requestId + 1 };
    },
    closeAdvice() {
      // Eine noch laufende Anfrage darf das Ergebnis danach nicht mehr in einen neuen Dialog schreiben
      this.advice = { target: null, state: "", result: null, error: "", requestId: this.advice.requestId + 1 };
    },
    runAdvice() {
      const requestId = this.advice.requestId;
      this.advice.state = "loading";
      offersService
        .getAdvice(this.advice.target.id)
        .then((result) => {
          if (requestId !== this.advice.requestId) return;
          this.advice.result = result;
          this.advice.state = "done";
        })
        .catch((e) => {
          if (requestId !== this.advice.requestId) return;
          const reason = e && e.response && e.response.data && e.response.data.reason;
          this.advice.error = reason && this.$te(`offers.advice.errors.${reason}`) ? this.$t(`offers.advice.errors.${reason}`) : this.$t("offers.advice.errors.webhook_error");
          this.advice.state = "error";
        });
    },
  },
};

const escapeHtml = (text) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Kleines Markdown der KI-Antwort (fett, Aufzaehlungen, Absaetze) als HTML; der Text wird vorher vollstaendig maskiert. */
function renderAdvice(markdown) {
  const lines = escapeHtml(markdown || "").split("\n");
  const html = [];
  let inList = false;
  lines.forEach((raw) => {
    const line = raw.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    if (bullet) {
      if (!inList) html.push("<ul>");
      inList = true;
      html.push(`<li>${bullet[1]}</li>`);
      return;
    }
    if (inList) html.push("</ul>");
    inList = false;
    if (line.trim()) html.push(`<p>${line}</p>`);
  });
  if (inList) html.push("</ul>");
  return html.join("");
}
</script>

<style scoped>
.mr-1 {
  margin-right: 15px;
}
.md-card .md-card-header.md-card-header-icon .material-symbols-outlined {
  width: 33px;
  height: 33px;
  text-align: center;
  line-height: 33px;
  color: #fff;
  margin: 0 !important;
  font-size: 28px !important;
}
.offers-calc-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  font-size: 12px;
  color: #777;
  margin-bottom: 4px;
}
.offers-calc-state .md-icon {
  font-size: 16px !important;
  width: 16px;
  min-width: 16px;
  height: 16px;
  animation: offers-spin 1.2s linear infinite;
}
@keyframes offers-spin {
  to {
    transform: rotate(360deg);
  }
}
.offers-calc-error {
  color: #f44336;
}
/* ohne feste Breite drueckt der Flex-Inhalt des md-icon-button das Bild auf 0 px */
.offer-ai-btn img {
  width: 16px;
  min-width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
}
.offer-plus {
  color: #7f915a;
  font-weight: 600;
}
.offer-minus {
  color: #c62828;
  font-weight: 600;
}
.offer-up {
  color: #7f915a;
}
.offer-down {
  color: #c62828;
}
.offer-sku-cell {
  font-weight: 600;
  white-space: nowrap;
  margin-bottom: 4px;
}
.table-offers >>> .offer-col-sku {
  min-width: 180px;
  max-width: 320px;
}
.offer-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.offer-chip {
  background: #f1f5e8;
  border: 1px solid #dce6c8;
  border-radius: 12px;
  padding: 1px 10px;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
}
.offer-chip:hover {
  background: #dce6c8;
}
.offer-muted {
  color: #bbb;
}
.offer-prices {
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  column-gap: 8px;
  row-gap: 1px;
  font-size: 12px;
  white-space: nowrap;
  width: max-content;
}
.offer-prices__label {
  color: #777;
}
.offer-prices__label--avk {
  color: #000;
  font-weight: 600;
}
.offer-prices__value {
  text-align: right;
}
.offer-prices__floor {
  color: #888;
  font-style: italic;
}
.offer-prices__diff {
  display: grid;
  grid-template-columns: 72px 56px;
  text-align: right;
  padding-left: 4px;
}
.offer-margins {
  border-collapse: collapse;
  font-size: 12px;
}
.offer-margins td {
  padding: 1px 0;
  border: 0;
  white-space: nowrap;
  vertical-align: middle;
}
.offer-margins__label {
  color: #777;
  padding-right: 8px !important;
  /* so schmal wie die Beschriftung: die Werte beginnen in jeder Zeile an derselben Stelle */
  width: 1%;
}
.offer-avk {
  font-weight: 700;
  color: #000;
  padding: 0 5px;
  border-radius: 3px;
}
.offer-avk--red {
  background: #ffcdd2;
  color: #b71c1c;
}
.offer-avk--orange {
  background: #ffe0b2;
  color: #a84300;
}
.offer-avk--yellow {
  background: #fff6c2;
  color: #6d5d00;
}
.offer-avk--green {
  background: #e7eed9;
  color: #414b2e;
}
.offer-price-trend {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 6px;
  border-radius: 50%;
  background: #9e9e9e;
  cursor: help;
}
.offer-price-trend .md-icon {
  font-size: 12px !important;
  width: 12px;
  min-width: 12px;
  height: 12px;
  color: #fff !important;
}
.offer-price-trend:hover {
  background: #7f915a;
}
.offer-advice-loading {
  display: flex;
  align-items: center;
  gap: 12px;
}
.offer-advice-error {
  color: #c62828;
}
.offer-advice-price {
  margin-bottom: 8px;
  font-size: 15px;
}
.offer-advice-situation {
  margin-bottom: 8px;
  font-size: 12px;
}
.offer-advice-chip {
  display: inline-block;
  margin-right: 6px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #e3f2fd;
  color: #1565c0;
}
.offer-advice-corrected {
  margin: 0 0 8px;
  font-size: 12px;
  color: #e65100;
}
.offer-advice-text {
  text-align: left;
}
.offer-advice-text >>> p {
  margin: 0 0 6px;
}
.offer-advice-text >>> ul {
  margin: 0 0 8px;
  padding-left: 20px;
}
.offer-advice-hint {
  font-size: 11px;
  color: #888;
  margin-top: 10px;
}
</style>

<style>
.offer-price-tooltip.md-tooltip {
  height: auto;
  max-width: none;
  padding: 8px 10px;
  background: #fff !important;
  color: #333 !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
  text-align: left;
}
.offer-price-tooltip__head {
  font-size: 11px;
  color: #777;
  margin-bottom: 4px;
}
.offer-price-changes {
  display: grid;
  grid-template-columns: auto auto auto;
  column-gap: 12px;
  font-size: 12px;
  margin-top: 6px;
  white-space: nowrap;
}
.offer-price-changes .text-right {
  text-align: right;
}
.offer-price-changes .offer-up {
  color: #7f915a;
}
.offer-price-changes .offer-down {
  color: #c62828;
}
/* Breit mit Abstand zu allen Seiten, der Text scrollt innerhalb (Trello KWDTD2pU, Update g) */
.offer-calc-modal.modal-container {
  max-width: 560px;
}
.offer-calc-dialog {
  text-align: left;
  min-height: 80px;
}
.offer-calc-info {
  display: inline-block;
  vertical-align: middle;
  margin-left: 4px;
  color: #aaa;
  cursor: pointer;
}
.offer-calc-info .md-icon {
  font-size: 16px !important;
  width: 16px;
  min-width: 16px;
  height: 16px;
}
.offer-calc-info:hover {
  color: #7f915a;
}
.offer-advice-modal.modal-container {
  max-width: 1100px;
  width: calc(100vw - 120px);
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
}
.offer-advice-modal.modal-container .modal-body {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}
</style>
